import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {catchError, Observable, of, switchMap, tap, throwError} from 'rxjs';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { STORAGE_NAME } from '../constants/constants';
import { NzI18nService } from 'ng-zorro-antd/i18n';
import {CookieService} from "@app/core/services/cookie.service";
import {AuthService} from "@app/core/services/auth.service";
import {StepUpService} from "@app/core/services/step-up.service";

@Injectable()
export class AppInterceptor implements HttpInterceptor {
  TOKEN_HEADER_KEY = 'Authorization';
  apiLastTime = null;

  constructor(private router: Router,
              private readonly i18n: NzI18nService,
              private toastService: ToastrService,
              private cookieService: CookieService,
              private auth: AuthService,
              private stepUp: StepUpService,
  ) {
    const timeoutSession = 15 * 60 * 1000;
    setInterval(() => {
      const now = new Date().valueOf();
      if (this.apiLastTime && now - this.apiLastTime > timeoutSession) {
        this.auth.logout();
      }
    }, 10000);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.apiLastTime = new Date().valueOf();
    const accessToken = this.cookieService.get(STORAGE_NAME.ACCESS_TOKEN);
    const url = req.url.toLowerCase();
    // Không gắn Bearer cho API public / session-cookie — token cũ sẽ khiến BE trả 401
    const skipBearer =
      url.includes('user/login') ||
      url.includes('user/logout') ||
      url.includes('oauth2/token') ||
      url.includes('/v1/otp/') ||
      url.includes('user/forgot-password') ||
      url.includes('user/change-password') ||
      url.includes('user/me/contact') ||
      url.includes('user/mfa/setup') ||
      url.includes('user/mfa/enable') ||
      url.includes('user/mfa/verify') ||
      url.includes('user/mfa/recovery');
    if (accessToken && !skipBearer) {
      req = this.addTokenHeader(req, accessToken);
    }
    req = req.clone({ headers: req.headers.set('Accept-Language', this.i18n.getLocale().locale) });

    return next.handle(req).pipe(
      switchMap((event: HttpEvent<any>) => {
        // Step-up 2FA: API trả HTTP 200 kèm code MFA_STEP_UP_REQUIRED → mở modal nhập mã → gửi lại request gốc
        if (event instanceof HttpResponse && event.body?.code === 'MFA_STEP_UP_REQUIRED') {
          return this.stepUp.ensureStepUp().pipe(switchMap(() => next.handle(req)));
        }
        return of(event);
      }),
      catchError((error: HttpErrorResponse) => {
        let messageError = 'Đã có lỗi xảy ra. Vui lòng thử lại sau!';
        if (error.status === 401) {
          // API session (confirm-contact / MFA login) không dùng Bearer — để component tự xử lý
          if (skipBearer) {
            return throwError(() => error);
          }
          if (this.cookieService.get(STORAGE_NAME.REFRESH_TOKEN) && !req.body?.includes('grant_type=refresh_token')) {
            return this.auth.refreshToken().pipe(
              switchMap((authRes: any) => {
                const newReq = this.addTokenHeader(req, authRes['access_token']); // Gắn token mới
                return next.handle(newReq); // Gửi lại request gốc
              })
            );
          } else {
            this.cookieService.deleteAll();
            // Không ép OAuth lại khi đang ở các trang public (đổi/quên MK, login…)
            const path = this.router.url.split('?')[0];
            const publicAuthPaths = [
              '/login',
              '/change-password',
              '/forgot-password',
              '/recovery-request',
              '/verify-2fa',
              '/setup-2fa',
              '/confirm-contact',
              '/callback'
            ];
            if (!publicAuthPaths.some(p => path === p || path.startsWith(p + '/'))) {
              this.router.navigateByUrl('/');
            }
          }
        } else if (error.status === 403) {
          this.router.navigateByUrl('/unauthorized').then();
        } else if (error.status === 400) {
          if (error.error instanceof Blob) {
            return new Observable<HttpEvent<any>>(observer => {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const jsonString = reader.result as string;
                  const jsonObject = JSON.parse(jsonString);
                  this.toastService.error(jsonObject?.message ?? messageError);
                  observer.error(jsonObject);
                } catch (e) {
                  this.toastService.error(messageError);
                  observer.error(error);
                }
              };
              reader.onerror = () => {
                this.toastService.error(messageError);
                observer.error(error);
              };
              reader.readAsText(error.error);
            });
          } else {
            messageError = error.error?.message ? error.error?.message : messageError;
            this.toastService.error(messageError);
          }
        } else if (error.status === 500) {
          this.toastService.error(messageError);
        }

        return throwError(() => error);
      })
    );
  }

  private addTokenHeader(request: HttpRequest<any>, accessToken: string) {
    return request.clone({ headers: request.headers.set(this.TOKEN_HEADER_KEY, 'Bearer ' + accessToken) });
  }
}
